import QRCode from 'qrcode'
import { jobServiceSummary } from './utils'

const MAX_FIELD_LENGTH = 96

function clean(value) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

function truncate(value, max = MAX_FIELD_LENGTH) {
  const text = clean(value)
  if (text.length <= max) return text
  return `${text.slice(0, Math.max(0, max - 1)).trim()}...`
}

function escapeHtml(value) {
  return clean(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function configuredSiteUrl() {
  const configured = clean(process.env.REACT_APP_SITE_URL)
  if (/^https?:\/\//i.test(configured)) return configured.replace(/\/+$/, '')
  return window.location.origin
}

export function jobTagUrl(job) {
  const token = clean(job?.job_tag_token)
  if (!token) return ''
  return `${configuredSiteUrl()}/job-tag/${encodeURIComponent(token)}`
}

export function tagCustomerName(customer) {
  const first = clean(customer?.first_name)
  const lastInitial = clean(customer?.last_name).charAt(0)
  if (first && lastInitial) return `${first} ${lastInitial}.`
  return first || 'Customer'
}

export function tagComponentSummary(component) {
  if (!component) return 'Booking job'
  return truncate([
    component.year,
    component.brand,
    component.model,
  ].map(clean).filter(Boolean).join(' ') || 'Component job', 64)
}

export function tagServiceSummary(job) {
  const summary = clean(jobServiceSummary(job))
    .replace(/\s+-\s+GBP\s+\d+(?:\.\d{1,2})?/gi, '')
    .replace(/\s+-\s+£\d+(?:\.\d{1,2})?/gi, '')
  return truncate(summary || 'Workshop service', 58)
}

export async function printJobTag({ job, customer, component, statusLabel }) {
  const url = jobTagUrl(job)
  if (!url) throw new Error('This job does not have a QR tag token yet. Apply schema v63 and reopen the job.')

  const printWindow = window.open('', '_blank')
  if (!printWindow) throw new Error('Could not open the print window. Allow pop-ups for Rebound and try again.')

  printWindow.document.write('<!DOCTYPE html><title>Preparing Job Tag</title><body>Preparing job tag...</body>')
  printWindow.document.close()

  const qrDataUrl = await QRCode.toDataURL(url, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 260,
    color: {
      dark: '#111111',
      light: '#ffffff',
    },
  })

  const jobNumber = escapeHtml(job?.job_number || 'JOB')
  const customerName = escapeHtml(tagCustomerName(customer))
  const itemSummary = escapeHtml(tagComponentSummary(component))
  const serviceSummary = escapeHtml(tagServiceSummary(job))
  const status = escapeHtml(statusLabel || job?.status || 'Status')
  const location = escapeHtml(job?.physical_location || 'Location not set')
  const safeQr = escapeHtml(qrDataUrl)

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Job Tag ${jobNumber}</title>
  <style>
    @page { size: 62mm 90mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; width: 62mm; height: 90mm; background: #fff; color: #111; }
    body { font-family: Arial, Helvetica, sans-serif; }
    .tag {
      width: 62mm;
      height: 90mm;
      padding: 3mm;
      display: flex;
      flex-direction: column;
      gap: 1.35mm;
      border: 0.25mm solid #111;
      overflow: hidden;
    }
    .brand {
      font-size: 12pt;
      font-weight: 900;
      letter-spacing: 0;
      line-height: 1;
    }
    .job-number {
      font-size: 15pt;
      font-weight: 900;
      line-height: 1.05;
      word-break: break-word;
    }
    .row { border-top: 0.2mm solid #ddd; padding-top: 0.85mm; }
    .label {
      display: block;
      font-size: 5.8pt;
      font-weight: 800;
      line-height: 1;
      text-transform: uppercase;
      color: #555;
      margin-bottom: 0.35mm;
    }
    .value {
      display: block;
      font-size: 8pt;
      font-weight: 800;
      line-height: 1.15;
      overflow-wrap: anywhere;
    }
    .value.small { font-size: 7.1pt; font-weight: 700; }
    .qr {
      margin-top: auto;
      display: grid;
      grid-template-columns: 18mm 1fr;
      gap: 2mm;
      align-items: end;
    }
    .qr img { display: block; width: 18mm; height: 18mm; }
    .scan-text { font-size: 5.8pt; line-height: 1.15; font-weight: 700; color: #333; }
    @media print {
      html, body { width: 62mm; height: 90mm; }
      .tag { border-color: #111; }
    }
  </style>
</head>
<body>
  <div class="tag">
    <div class="brand">REBOUND</div>
    <div class="job-number">${jobNumber}</div>
    <div class="row"><span class="label">Customer</span><span class="value">${customerName}</span></div>
    <div class="row"><span class="label">Bike / Component</span><span class="value small">${itemSummary}</span></div>
    <div class="row"><span class="label">Service</span><span class="value small">${serviceSummary}</span></div>
    <div class="row"><span class="label">Status</span><span class="value">${status}</span></div>
    <div class="row"><span class="label">Location</span><span class="value">${location}</span></div>
    <div class="qr">
      <img src="${safeQr}" alt="Job QR code">
      <div class="scan-text">Scan to open the internal Rebound job record.</div>
    </div>
  </div>
  <script>window.onload=function(){window.focus();window.print();}<\/script>
</body>
</html>`

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
}
